<template>
  <div class="sales-trend">
    <!-- Quick periods + grouping -->
    <div class="d-flex flex-wrap justify-content-between column-gap-3">
      <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />
      <ReportChoices label="View by" icon="ti-chart-bar" :choices="GROUPS" :active="filters.group" @select="setGroup" />
    </div>

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter">
          <label>Sold by</label>
          <FormDropdown v-model="filters.cashier" :options="cashierOptions" placeholder="Everyone" clearable />
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-chart-line"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Sales</div>
          <div class="rp-card-value">{{ formatMoney(summary.sales) }}</div>
          <div class="rp-card-sub">{{ plural(summary.invoice_count, 'invoice') }} · avg {{ formatMoney(summary.avg_invoice) }}</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-calendar-stats"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Average per Day</div>
          <div class="rp-card-value">{{ formatMoney(summary.avg_per_day) }}</div>
          <div class="rp-card-sub">sold on {{ summary.active_days }} of {{ plural(summary.days, 'day') }}</div>
        </div>
      </div>
      <div class="rp-card tone-green">
        <span class="rp-card-icon"><i class="ti ti-flame"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Peak {{ peakLabel }}</div>
          <div class="rp-card-value text-truncate">{{ summary.peak ? summary.peak.label : '—' }}</div>
          <div class="rp-card-sub">
            <template v-if="summary.peak">
              {{ formatMoney(summary.peak.sales) }} · {{ plural(summary.peak.invoice_count, 'invoice') }}
            </template>
            <template v-else>No sales in this period</template>
          </div>
        </div>
      </div>
      <div class="rp-card" :class="changeTone">
        <span class="rp-card-icon"><i class="ti" :class="changeIcon"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">vs Previous Period</div>
          <div class="rp-card-value">{{ changeText }}</div>
          <div class="rp-card-sub">
            {{ formatMoney(summary.previous.sales) }} on {{ formatDate(summary.previous.date_from) }} –
            {{ formatDate(summary.previous.date_to) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights st-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Sales by {{ groupLabel.toLowerCase() }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Sales</span>
            <span><i class="rp-dot" :style="{ background: RETURN_COLOR }"></i>Returns</span>
            <span><i class="rp-dot" :style="{ background: COUNT_COLOR }"></i>Invoices</span>
          </span>
        </div>
        <div v-show="rows.length" class="rp-chart st-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!rows.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Highlights</div>
        <ul class="rp-highlights">
          <li v-for="item in highlights" :key="item.label" :class="item.tone">
            <span class="hl-icon"><i class="ti" :class="item.icon"></i></span>
            <div class="min-w-0">
              <small class="text-muted d-block">{{ item.label }}</small>
              <div class="fw-semibold text-truncate">{{ item.value }}</div>
              <small class="text-muted">{{ item.detail }}</small>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Busiest hours heatmap -->
    <div class="rp-panel mb-3" :class="{ 'is-loading': loading }">
      <div class="rp-panel-title d-flex justify-content-between align-items-center">
        <span>Busiest hours <small class="text-muted fw-normal">— sales by day of week and hour</small></span>
        <span class="st-scale">less <i v-for="step in 5" :key="step" :style="{ opacity: step / 5 }"></i> more</span>
      </div>
      <div v-if="Number(summary.heat_max)" class="table-responsive">
        <table class="st-heat">
          <thead>
            <tr>
              <th></th>
              <th v-for="hour in hoursShown" :key="hour">{{ shortHour(hour) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in summary.heatmap" :key="day.weekday">
              <th>{{ day.label.slice(0, 3) }}</th>
              <td v-for="hour in hoursShown" :key="hour"
                  :title="`${day.label} ${shortHour(hour)}: ${formatMoney(day.hours[hour])}`">
                <span :style="heatStyle(day.hours[hour])"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted small mb-0">No sales in this period.</p>
    </div>

    <!-- Buckets table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button type="button" class="rp-pill" :class="{ active: !hideEmpty }" @click="hideEmpty = false">
            Every {{ groupLabel.toLowerCase() }} <span class="rp-pill-count">{{ formatInt(rows.length) }}</span>
          </button>
          <button type="button" class="rp-pill" :class="{ active: hideEmpty }" @click="hideEmpty = true">
            With sales <span class="rp-pill-count">{{ formatInt(activeCount) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in TREND_PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in tableColumns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key" :class="{ 'st-empty': !row.invoice_count && !Number(row.returns) }">
              <td class="text-nowrap fw-semibold">
                {{ rowLabel(row) }}
                <span v-if="row.weekday !== undefined" class="rp-sub">{{ plural(row.days, 'day') }} in period</span>
                <span v-else-if="row.date && filters.group === 'day'" class="rp-sub">{{ weekdayOf(row.date) }}</span>
              </td>
              <td class="text-center"><span class="rp-count" :class="{ zero: !row.invoice_count }">{{ row.invoice_count }}</span></td>
              <td class="text-end">{{ Number(row.qty) ? formatQty(row.qty) : '—' }}</td>
              <td class="text-end fw-semibold">{{ Number(row.sales) ? formatMoney(row.sales) : '—' }}</td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.discount) }">
                {{ Number(row.discount) ? `−${formatMoney(row.discount)}` : '—' }}
              </td>
              <td class="text-end" :class="{ 'text-muted': !Number(row.vat) }">{{ Number(row.vat) ? formatMoney(row.vat) : '—' }}</td>
              <td class="text-end" :class="Number(row.returns) ? 'text-danger' : 'text-muted'">
                {{ Number(row.returns) ? `−${formatMoney(row.returns)}` : '—' }}
              </td>
              <td class="text-end fw-semibold">{{ formatMoney(row.net) }}</td>
              <td class="text-end" :class="netClass(row.profit)">{{ Number(row.profit) ? formatMoney(row.profit) : '—' }}</td>
              <td v-if="showPerDay" class="text-end">{{ Number(row.avg_per_day) ? formatMoney(row.avg_per_day) : '—' }}</td>
              <td class="text-end">{{ Number(row.avg_invoice) ? formatMoney(row.avg_invoice) : '—' }}</td>
              <td class="text-end st-share">
                {{ row.share }}%
                <div class="rp-bar-track mt-1"><div class="rp-bar-fill" :style="{ width: `${sharePercent(row)}%` }"></div></div>
              </td>
            </tr>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="tableColumns.length">
                <div class="rp-empty">
                  <i class="ti ti-chart-line"></i>
                  <div class="fw-semibold">No sales found</div>
                  <small class="text-muted">Try another period, customer or cashier.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th class="text-end">Total</th>
              <th class="text-center">{{ formatInt(summary.invoice_count) }}</th>
              <th class="text-end">{{ formatQty(summary.qty) }}</th>
              <th class="text-end">{{ formatMoney(summary.sales) }}</th>
              <th class="text-end">{{ Number(summary.discount) ? `−${formatMoney(summary.discount)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.vat) }}</th>
              <th class="text-end text-danger">{{ Number(summary.returns) ? `−${formatMoney(summary.returns)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.net) }}</th>
              <th class="text-end" :class="netClass(summary.profit)">{{ formatMoney(summary.profit) }}</th>
              <th v-if="showPerDay" class="text-end">{{ formatMoney(summary.avg_per_day) }}</th>
              <th class="text-end">{{ formatMoney(summary.avg_invoice) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Sales are as invoiced, at the time the invoice was made; returns count when they were made. Net = sales − returns.
      The previous period is the same number of days just before.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { COUNT_COLOR, RETURN_COLOR, SALES_COLOR } from './salesReportMixin';

const GROUPS = [
  { key: 'hour', label: 'Hour' },
  { key: 'weekday', label: 'Day of week' },
  { key: 'day', label: 'Date' },
  { key: 'month', label: 'Month' },
];
const TREND_PAGE_SIZES = [10, 24, 31, 50, 100];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const EMPTY_SUMMARY = {
  qty: 0, sales: 0, discount: 0, vat: 0, returns: 0, net: 0, profit: 0, group: 'day', group_label: 'Date',
  invoice_count: 0, days: 0, active_days: 0, avg_per_day: 0, avg_invoice: 0, margin: 0, peak: null,
  busiest_hour: null, busiest_weekday: null, best_day: null, heatmap: [], heat_max: 0,
  previous: { date_from: null, date_to: null, sales: 0, invoice_count: 0, change: null },
};
const COLUMNS = [
  { key: 'key', label: 'Period' },
  { key: 'invoice_count', label: 'Invoices', class: 'text-center', numeric: true },
  { key: 'qty', label: 'Qty', class: 'text-end', numeric: true },
  { key: 'sales', label: 'Sales', class: 'text-end', numeric: true },
  { key: 'discount', label: 'Discount', class: 'text-end', numeric: true },
  { key: 'vat', label: 'VAT', class: 'text-end', numeric: true },
  { key: 'returns', label: 'Returns', class: 'text-end', numeric: true },
  { key: 'net', label: 'Net Sales', class: 'text-end', numeric: true },
  { key: 'profit', label: 'Profit', class: 'text-end', numeric: true },
  { key: 'avg_per_day', label: 'Avg / Day', class: 'text-end', numeric: true },
  { key: 'avg_invoice', label: 'Avg Invoice', class: 'text-end', numeric: true },
  { key: 'share', label: 'Share', class: 'text-end', numeric: true },
];

export default {
  name: 'SalesTrendReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.sales_trend_report.get,
  fileName: 'sales_trend',
  columns: COLUMNS,

  data() {
    return {
      filters: {
        ...defaultPeriod(), group: 'day', customer: null, cashier: null,
        warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      hideEmpty: false,
      pageSize: 31,
      GROUPS,
      TREND_PAGE_SIZES,
      SALES_COLOR,
      RETURN_COLOR,
      COUNT_COLOR,
    };
  },

  computed: {
    hasActiveFilters() {
      const { customer, cashier, group } = this.filters;
      return this.periodChanged || group !== 'day' || !!(customer || cashier);
    },

    groupLabel() {
      return GROUPS.find((g) => g.key === this.filters.group)?.label || 'Date';
    },

    peakLabel() {
      return { hour: 'Hour', weekday: 'Day', day: 'Day', month: 'Month' }[this.summary.group] || 'Day';
    },

    /** Avg / day only means something when a row covers several days. */
    showPerDay() {
      return this.summary.group !== 'day';
    },

    tableColumns() {
      return this.columns
        .filter((c) => c.key !== 'avg_per_day' || this.showPerDay)
        .map((c) => (c.key === 'key' ? { ...c, label: this.summary.group_label } : c));
    },

    changeText() {
      const change = this.summary.previous.change;
      if (change === null || change === undefined) return Number(this.summary.sales) ? 'New' : '—';
      const n = Number(change);
      return `${n > 0 ? '+' : ''}${n}%`;
    },

    changeTone() {
      const n = Number(this.summary.previous.change);
      if (!n) return 'tone-navy';
      return n > 0 ? 'tone-green' : 'tone-red';
    },

    changeIcon() {
      const n = Number(this.summary.previous.change);
      if (!n) return 'ti-equal';
      return n > 0 ? 'ti-trending-up' : 'ti-trending-down';
    },

    highlights() {
      const s = this.summary;
      return [
        {
          label: 'Busiest hour', icon: 'ti-clock', tone: 'tone-orange',
          value: s.busiest_hour ? s.busiest_hour.label : '—',
          detail: s.busiest_hour ? `${this.formatMoney(s.busiest_hour.sales)} · ${this.plural(s.busiest_hour.count, 'invoice')}` : 'No sales',
        },
        {
          label: 'Busiest day of the week', icon: 'ti-calendar-week', tone: 'tone-blue',
          value: s.busiest_weekday ? s.busiest_weekday.label : '—',
          detail: s.busiest_weekday ? this.formatMoney(s.busiest_weekday.sales) : 'No sales',
        },
        {
          label: 'Best day', icon: 'ti-trophy', tone: 'tone-green',
          value: s.best_day ? `${this.formatDate(s.best_day.date)} (${this.weekdayOf(s.best_day.date)})` : '—',
          detail: s.best_day ? this.formatMoney(s.best_day.sales) : 'No sales',
        },
        {
          label: 'Gross margin', icon: 'ti-percentage', tone: 'tone-teal',
          value: `${s.margin}%`, detail: `${this.formatMoney(s.profit)} profit on net sales`,
        },
      ];
    },

    /** Opening hours only: from the first to the last hour with a sale (at least 8 AM – 10 PM). */
    hoursShown() {
      const active = [];
      this.summary.heatmap.forEach((day) => day.hours.forEach((v, h) => { if (Number(v)) active.push(h); }));
      const first = Math.min(8, ...active);
      const last = Math.max(21, ...active);
      return Array.from({ length: last - first + 1 }, (_, i) => first + i);
    },

    maxShare() {
      return Math.max(0, ...this.rows.map((r) => Number(r.share) || 0));
    },

    activeRows() {
      return this.rows.filter((r) => r.invoice_count || Number(r.returns));
    },

    activeCount() {
      return this.activeRows.length;
    },

    visibleRows() {
      const rows = this.hideEmpty ? this.activeRows : this.rows;
      return this.sortKey ? this.sortRows(rows) : rows;
    },
  },

  watch: {
    'filters.cashier'() { this.onFilterChange(); },
    'filters.group'() { this.onFilterChange(); },
    hideEmpty() { this.page = 1; },
  },

  methods: {
    extraParams() {
      const { cashier, group } = this.filters;
      return {
        ...this.periodParams(),
        ...this.customerParams(),
        ...(cashier ? { cashier } : {}),
        group,
      };
    },

    resetExtraFilters() {
      this.sortKey = null;
      this.hideEmpty = false;
      return { ...defaultPeriod(this.today), group: 'day', customer: null, cashier: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      const rows = this.rows;
      this.$nextTick(() => this.drawBars('trend', this.$refs.trendChart, rows.map((r) => this.chartLabel(r)), [
        { label: 'Sales', data: rows.map((r) => r.sales), color: SALES_COLOR },
        { label: 'Returns', data: rows.map((r) => r.returns), color: RETURN_COLOR },
        { label: 'Invoices', data: rows.map((r) => r.invoice_count), color: COUNT_COLOR, count: true },
      ]));
    },

    setGroup(choice) {
      this.sortKey = null;
      this.filters.group = choice.key;
    },

    rowLabel(row) {
      if (this.summary.group === 'day') return this.formatDate(row.date);
      return row.label;
    },

    chartLabel(row) {
      const group = this.summary.group;
      if (group === 'hour') return this.shortHour(row.hour);
      if (group === 'weekday') return row.label.slice(0, 3);
      if (group === 'day') return row.date.slice(8, 10) + '/' + row.date.slice(5, 7);
      return row.label;
    },

    shortHour(hour) {
      const h = Number(hour);
      return `${h % 12 || 12}${h < 12 ? 'a' : 'p'}`;
    },

    weekdayOf(iso) {
      return WEEKDAY_NAMES[new Date(`${iso}T00:00:00`).getDay()].slice(0, 3);
    },

    heatStyle(value) {
      const max = Number(this.summary.heat_max) || 0;
      const v = Number(value) || 0;
      if (!v || !max) return {};
      return { background: SALES_COLOR, opacity: 0.18 + 0.82 * (v / max) };
    },

    sharePercent(row) {
      return this.maxShare ? Math.round(((Number(row.share) || 0) / this.maxShare) * 100) : 0;
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(this.rowLabel(r))}</td>
          <td class="num">${r.invoice_count}</td>
          <td class="num">${this.formatQty(r.qty)}</td>
          <td class="num">${this.formatMoney(r.sales)}</td>
          <td class="num">${this.formatMoney(r.discount)}</td>
          <td class="num">${this.formatMoney(r.vat)}</td>
          <td class="num">${this.formatMoney(r.returns)}</td>
          <td class="num">${this.formatMoney(r.net)}</td>
          <td class="num">${this.formatMoney(r.profit)}</td>
          <td class="num">${this.formatMoney(r.avg_invoice)}</td>
          <td class="num">${r.share}%</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>${escapeHtml(s.group_label)}</th><th class="num">Invoices</th>
          <th class="num">Qty</th><th class="num">Sales</th><th class="num">Discount</th><th class="num">VAT</th>
          <th class="num">Returns</th><th class="num">Net Sales</th><th class="num">Profit</th>
          <th class="num">Avg Invoice</th><th class="num">Share</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td class="num">Total</td><td class="num">${s.invoice_count}</td>
          <td class="num">${this.formatQty(s.qty)}</td><td class="num">${this.formatMoney(s.sales)}</td>
          <td class="num">${this.formatMoney(s.discount)}</td><td class="num">${this.formatMoney(s.vat)}</td>
          <td class="num">${this.formatMoney(s.returns)}</td><td class="num">${this.formatMoney(s.net)}</td>
          <td class="num">${this.formatMoney(s.profit)}</td><td class="num">${this.formatMoney(s.avg_invoice)}</td>
          <td></td></tr></tfoot></table>`;
      this.openPrint(
        'Hourly / Daily Sales',
        [
          [this.periodText(), `By: ${this.groupLabel}`, this.customerText(), this.cashierText(), this.storeText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Sales', `${this.formatMoney(s.sales)} · ${this.plural(s.invoice_count, 'invoice')}`],
          ['Average per Day', this.formatMoney(s.avg_per_day)],
          [`Peak ${this.peakLabel}`, escapeHtml(s.peak ? s.peak.label : '—')],
          ['vs Previous Period', escapeHtml(this.changeText)],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.rp-insights.st-insights {
  grid-template-columns: 2.2fr 1fr;
}

.st-chart {
  height: 220px;
}

.st-heat {
  width: 100%;
  border-collapse: separate;
  border-spacing: 3px;
  font-size: 0.7rem;
}

.st-heat th {
  color: #9AA1A9;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
}

.st-heat tbody th {
  width: 36px;
  padding-right: 0.4rem;
  text-align: left;
}

.st-heat td {
  min-width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 4px;
  background: #F4F6F8;
}

.st-heat td span {
  display: block;
  width: 100%;
  height: 22px;
  border-radius: 4px;
}

.st-scale {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #9AA1A9;
  font-size: 0.72rem;
  font-weight: 400;
}

.st-scale i {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: #FE9F43;
}

.st-share {
  min-width: 90px;
}

.st-share .rp-bar-track {
  height: 5px;
}

.rp-table > tbody > tr.st-empty > td {
  color: #B4BAC1;
}

@media (max-width: 1199.98px) {
  .rp-insights.st-insights { grid-template-columns: 1fr; }
  .rp-insights.st-insights > :last-child { grid-column: auto; }
}
</style>
